import type { CSSProperties, FC, ReactNode } from 'react'

import Link from 'next/link'
import {
    Clock,
    Mail,
    MapPin,
    Phone
} from 'lucide-react'
import { ButtonLink } from '@/components/generals/button'
import { Scallop } from '@/components/generals/scallop'
import { Sticker } from '@/components/generals/sticker'
import { contactDetails, navLinks, siteConfig } from '@/constants/site'

export const Footer: FC = (): ReactNode => {
    return (
        <footer className='on-dark relative bg-plum-deep text-cream'>
            <Scallop className='text-plum-deep' />

            <div className='mx-auto grid max-w-6xl gap-12 px-5 pt-20 pb-14 sm:px-8 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_0.7fr]'>
                <div>
                    <p className='font-heading text-[clamp(2.5rem,5vw,3.75rem)] leading-[0.95]'>
                        Ready when you are.
                    </p>

                    <p className='mt-4 max-w-sm text-lg text-cream/75'>
                        Tell us about your next big thing and we'll get back to you within one business day.
                    </p>

                    <ButtonLink href='/#contact' variant='dark' className='mt-8'>
                        Start a project
                    </ButtonLink>
                </div>

                <div>
                    <h2 className='font-heading text-xl tracking-wide text-butter'>
                        Say hello
                    </h2>

                    <ul className='mt-5 space-y-4 text-cream/85'>
                        <li className='flex gap-3'>
                            <Phone className='mt-0.5 size-5 shrink-0 text-butter' aria-hidden='true' />

                            <a href={contactDetails.phoneHref} className='underline-offset-4 transition-colors hover:text-butter hover:underline'>
                                {contactDetails.phone}
                            </a>
                        </li>

                        <li className='flex gap-3'>
                            <Mail className='mt-0.5 size-5 shrink-0 text-butter' aria-hidden='true' />

                            <a href={`mailto:${contactDetails.email}`} className='underline-offset-4 transition-colors hover:text-butter hover:underline'>
                                {contactDetails.email}
                            </a>
                        </li>

                        <li className='flex gap-3'>
                            <MapPin className='mt-0.5 size-5 shrink-0 text-butter' aria-hidden='true' />

                            <address className='not-italic'>
                                {contactDetails.street}
                                <br />
                                {contactDetails.city}
                            </address>
                        </li>

                        <li className='flex gap-3'>
                            <Clock className='mt-0.5 size-5 shrink-0 text-butter' aria-hidden='true' />
                            <span>{contactDetails.hours}</span>
                        </li>
                    </ul>
                </div>

                <nav aria-label='Footer'>
                    <h2 className='font-heading text-xl tracking-wide text-butter'>
                        Explore
                    </h2>

                    <ul className='mt-5 space-y-3'>
                        {navLinks.map((link) => (
                            <li key={link.href}>
                                <Link href={link.href} className='inline-block text-cream/85 underline-offset-4 transition-all duration-200 hover:translate-x-1 hover:text-butter hover:underline'>
                                    {link.label}
                                </Link>
                            </li>
                        ))}

                        <li>
                            <Link href='/#contact' className='inline-block text-cream/85 underline-offset-4 transition-all duration-200 hover:translate-x-1 hover:text-butter hover:underline'>
                                Contact
                            </Link>
                        </li>
                    </ul>
                </nav>
            </div>

            <div className='mx-auto flex max-w-6xl flex-col gap-2 border-t border-cream/15 px-5 py-6 text-sm text-cream/60 sm:flex-row sm:justify-between sm:px-8'>
                <p>
                    &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. All rights reserved.
                </p>

                <p>
                    Made with big ideas in Portland, Oregon.
                </p>
            </div>

            <div aria-hidden='true' className='relative overflow-hidden'>
                <p className='reveal translate-y-[10%] text-center font-heading text-[29vw] leading-[0.8] tracking-[-0.01em] text-cream uppercase'>
                    {'Huzzah'.split('').map((letter, index) => (
                        <span key={`${letter}-${index}`} className='bouncy-letter'>
                            <span className='wave-letter inline-block' style={{ '--wave-delay': `${index * 140}ms` } as CSSProperties}>
                                {letter}
                            </span>
                        </span>
                    ))}
                </p>

                <Sticker
                    label='Hip hip!'
                    shape='burst'
                    colorClassName='text-butter'
                    className='absolute top-[8%] left-[6%] size-[clamp(4rem,11vw,9rem)] -rotate-12 text-[clamp(0.9rem,2vw,1.6rem)] motion-safe:animate-float'
                />

                <Sticker
                    label='Hooray!'
                    shape='flower'
                    colorClassName='text-bubblegum'
                    className='absolute top-[18%] right-[5%] size-[clamp(4rem,11vw,9rem)] rotate-12 text-[clamp(0.9rem,2vw,1.6rem)] motion-safe:animate-wiggle'
                />
            </div>
        </footer>
    )
}
